import type { SemanticAnalysis, SemanticElements } from './types';

const SUBJECT_KEYWORDS = [
  'man', 'woman', 'girl', 'boy', 'child', 'person', 'warrior', 'knight', 'soldier',
  'artist', 'dancer', 'chef', 'driver', 'cat', 'dog', 'wolf', 'lion', 'bird', 'robot',
  'رجل', 'امرأة', 'بنت', 'ولد', 'شخص', 'محارب', 'طفل', 'قطة', 'كلب', 'طائر', 'روبوت',
];

const ACTION_KEYWORDS = [
  'standing', 'sitting', 'walking', 'running', 'jumping', 'flying', 'looking', 'watching',
  'holding', 'fighting', 'dancing', 'smiling', 'crying', 'reading', 'writing',
  'واقف', 'واقفة', 'واقفًا', 'جالس', 'جالسة', 'يمشي', 'يركض', 'يطير', 'ينظر', 'يحمل', 'يقاتل', 'يرقص',
];

const ENVIRONMENT_KEYWORDS = [
  'street', 'city', 'alley', 'rooftop', 'desert', 'forest', 'jungle', 'beach', 'ocean',
  'mountain', 'room', 'office', 'studio', 'temple', 'castle', 'market', 'cafe', 'space',
  'شارع', 'مدينة', 'سطح', 'صحراء', 'غابة', 'شاطئ', 'بحر', 'جبل', 'غرفة', 'مكتب', 'مقهى', 'سوق',
];

const TIME_LIGHTING_KEYWORDS = [
  'sunrise', 'sunset', 'golden hour', 'blue hour', 'daylight', 'night', 'moonlight',
  'rain', 'fog', 'mist', 'neon', 'soft light', 'hard light', 'dramatic light',
  'sunny', 'cloudy', 'storm',
  'فجر', 'غروب', 'نهار', 'ليل', 'ضوء', 'إضاءة', 'قمر', 'شمس', 'مطر', 'ضباب',
];

const MOOD_STYLE_KEYWORDS = [
  'cinematic', 'photorealistic', 'realistic', 'stylized', 'anime', 'noir', 'cyberpunk',
  'dramatic', 'epic', 'moody', 'minimal', 'vintage', 'filmic',
  'سينمائي', 'واقعي', 'أنمي', 'درامي', 'ملحمي', 'كلاسيكي', 'مودي',
];

const EN_ENVIRONMENT_PREP = /\b(?:in|at|inside|on|near|under|within)\s+([a-z0-9\s-]{3,60})/i;
const AR_ENVIRONMENT_PREP = /(?:في|داخل|على|وسط|عند)\s+([^\n,.]{2,50})/i;
const EN_ACTION_PATTERN = /\b(?:is|are|was|were)\s+([a-z\s-]{3,40})/i;
const AR_ACTION_PATTERN = /(?:وهو|وهي|يكون|تكون)\s+([^\n,.]{2,40})/i;

const normalizeText = (value: string): string => value
  .replace(/\s+/g, ' ')
  .replace(/[،؛]/g, ',')
  .trim();

const cleanPhrase = (value: string): string => value
  .replace(/\s+/g, ' ')
  .replace(/^[,.\s-]+/, '')
  .replace(/[,.\s-]+$/, '')
  .trim();

const detectLanguage = (value: string): 'ar' | 'en' | 'mixed' => {
  const hasArabic = /[\u0600-\u06FF]/.test(value);
  const hasLatin = /[A-Za-z]/.test(value);
  if (hasArabic && hasLatin) return 'mixed';
  if (hasArabic) return 'ar';
  return 'en';
};

const pickByKeywords = (source: string, keywords: string[]): string => {
  const hits = keywords
    .filter((keyword) => source.includes(keyword.toLowerCase()))
    .sort((a, b) => b.length - a.length);

  return hits[0] || '';
};

const extractSubject = (normalized: string): string => {
  const byKeyword = pickByKeywords(normalized, SUBJECT_KEYWORDS);
  if (byKeyword) return byKeyword;

  const firstChunk = cleanPhrase(normalized.split(/[,.!\n]/)[0] || '');
  const withoutArticles = firstChunk.replace(/^(a|an|the)\s+/i, '').trim();
  const words = withoutArticles.split(/\s+/).slice(0, 5).join(' ').trim();
  return words || 'main subject';
};

const extractAction = (normalized: string): string => {
  const byKeyword = pickByKeywords(normalized, ACTION_KEYWORDS);
  if (byKeyword) return byKeyword;

  const enMatch = normalized.match(EN_ACTION_PATTERN)?.[1];
  if (enMatch) return cleanPhrase(enMatch);

  const arMatch = normalized.match(AR_ACTION_PATTERN)?.[1];
  if (arMatch) return cleanPhrase(arMatch);

  return '';
};

const extractEnvironment = (normalized: string): string => {
  const byKeyword = pickByKeywords(normalized, ENVIRONMENT_KEYWORDS);
  if (byKeyword) return byKeyword;

  const enMatch = normalized.match(EN_ENVIRONMENT_PREP)?.[1];
  if (enMatch) return cleanPhrase(enMatch.split(/[,.]/)[0] || '');

  const arMatch = normalized.match(AR_ENVIRONMENT_PREP)?.[1];
  if (arMatch) return cleanPhrase(arMatch.split(/[,.]/)[0] || '');

  return '';
};

const extractTimeLighting = (normalized: string): string => {
  const byKeyword = pickByKeywords(normalized, TIME_LIGHTING_KEYWORDS);
  return byKeyword || '';
};

const extractMoodStyle = (normalized: string): string => {
  const byKeyword = pickByKeywords(normalized, MOOD_STYLE_KEYWORDS);
  return byKeyword || '';
};

const calcConfidence = (elements: SemanticElements): number => {
  const values = Object.values(elements).filter(Boolean).length;
  return Math.min(100, Math.round((values / 5) * 100));
};

export const analyzeSemantics = (description: string): SemanticAnalysis => {
  const sourceText = normalizeText(description);
  const lowered = sourceText.toLowerCase();

  const elements: SemanticElements = {
    subject: extractSubject(lowered),
    action: extractAction(lowered),
    environment: extractEnvironment(lowered),
    timeLighting: extractTimeLighting(lowered),
    moodStyle: extractMoodStyle(lowered),
  };

  const missing = (Object.entries(elements)
    .filter(([, value]) => !value)
    .map(([key]) => key)) as Array<keyof SemanticElements>;

  return {
    language: detectLanguage(sourceText),
    sourceText,
    elements,
    missing,
    confidence: calcConfidence(elements),
  };
};
